/**
 * Touch 横向滑动监听 (支持 swipe + 区分点击)
 * - 横向偏移 > 8px 且超过纵向偏移 = 进入 swipe 模式
 * - 偏移超过 threshold 触发 onSwipeLeft / onSwipeRight
 * - 没有偏移 = 普通 click
 */
import { useRef, useState, useCallback } from 'react';

export interface SwipeHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
}

export interface UseSwipeOpts {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number; // 触发 swipe 的最小偏移 (px)
  maxOffset?: number; // 视觉上的最大偏移 (px)
}

export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = 80,
  maxOffset = 100,
}: UseSwipeOpts): {
  offset: number;
  swiping: boolean;
  handlers: SwipeHandlers;
  reset: () => void;
} {
  const [offset, setOffset] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startT = useRef(0);
  const moved = useRef(false);
  const lastDirection = useRef<'h' | 'v' | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    startT.current = Date.now();
    moved.current = false;
    lastDirection.current = null;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    // 第一次 move 决定方向
    if (lastDirection.current === null) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        lastDirection.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        moved.current = true;
      }
    }
    // 只在横向模式时偏移
    if (lastDirection.current === 'h') {
      // 限制最大偏移
      const clamped = Math.max(-maxOffset, Math.min(maxOffset, dx));
      setOffset(clamped);
      setSwiping(true);
    }
  }, [maxOffset]);

  const onTouchEnd = useCallback(
    (_e: React.TouchEvent) => {
      if (lastDirection.current === 'h') {
        if (offset < -threshold) {
          onSwipeLeft?.();
        } else if (offset > threshold) {
          onSwipeRight?.();
        }
      }
      setOffset(0);
      setSwiping(false);
      lastDirection.current = null;
    },
    [offset, threshold, onSwipeLeft, onSwipeRight]
  );

  return {
    offset,
    swiping,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
    reset: () => {
      setOffset(0);
      setSwiping(false);
    },
  };
}
